import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Trash2, Plus } from "lucide-react";
import Switch from "./switch";

export default function DynamicEstadoPagoBuilder({ fields, setFields }) {
    const { t } = useTranslation();

    const inputTypeOptions = [
        { value: "text", label: t("label_texto") },
        { value: "number", label: t("label_numero") },
        { value: "date", label: t("label_fecha") },
        { value: "time", label: t("label_hora") },
        { value: "date_time", label: t("label_date_time") },
    ];

    const [selectedType, setSelectedType] = useState(null);

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const handleAddField = () => {
        if (!selectedType) return;

        const newField = {
            id: `field-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            type: selectedType.value,
            label: "",
            value: "",
            enabled: true,
            order: fields.length,
        };

        setFields([...fields, newField]);
        setSelectedType(null);
    };

    const handleDragEnd = (event) => {
        const { active, over } = event;

        if (over && active.id !== over.id) {
            setFields((items) => {
                const oldIndex = items.findIndex((item) => item.id === active.id);
                const newIndex = items.findIndex((item) => item.id === over.id);
                const newFields = arrayMove(items, oldIndex, newIndex);
                // Update order property
                return newFields.map((field, index) => ({
                    ...field,
                    order: index,
                }));
            });
        }
    };

    const handleDeleteField = (id) => {
        setFields(fields.filter((field) => field.id !== id));
    };

    const handleToggleEnabled = (id) => {
        setFields(
            fields.map((field) =>
                field.id === id ? { ...field, enabled: !field.enabled } : field
            )
        );
    };

    /*const handleFieldChange = (id, key, value) => {
        // Convert to lowercase and filter to only allow lowercase letters (a-z) and underscore (_)
        let filteredValue = value;
        if (key === "label") {
            // Convert to lowercase first, then filter out any characters that aren't a-z or _
            filteredValue = value.toLowerCase().replace(/[^a-z_]/g, "");
        }

        setFields(
            fields.map((field) =>
                field.id === id ? { ...field, [key]: filteredValue } : field
            )
        );
    };*/

    const handleFieldChange = (id, key, value) => {
        // If key is not 'key' or 'label', return early
        if (key !== "key" && key !== "label") {
            return;
        }
        let filteredValue = value;

        if (key === "key") {
            // Convert to lowercase first, then filter out any characters that aren't a-z or _
            filteredValue = value.toLowerCase().replace(/[^a-z_]/g, "");
        }
        if (key === "label") {
            // Allow only numbers, alphabet letters (uppercase and lowercase), comma, dot, parentheses, and spaces
            filteredValue = value.replace(/[^a-zA-Z0-9,()._ ]/g, "");

            // Also update the 'key' field with the same value but applying key validation
            const keyValue = value.toLowerCase().replace(/[^a-z_]/g, "").replace(/\s+/g, "_");

            setFields(
                fields.map((field) =>
                    field.id === id
                        ? { ...field, label: filteredValue, key: keyValue }
                        : field
                )
            );
            return;
        }

        setFields(
            fields.map((field) =>
                field.id === id ? { ...field, [key]: filteredValue } : field
            )
        );
    };

    return (
        <div className="w-full">
            {/* Add Field Section */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t("label_agregar_campo")}
                </label>
                <div className="flex gap-2">
                    <div className="flex-1">
                        <Select
                            options={inputTypeOptions}
                            value={selectedType}
                            onChange={setSelectedType}
                            placeholder={t("placeholder_seleccione_tipo")}
                            isSearchable={false}
                            className="react-select-container"
                            classNamePrefix="react-select"
                        />
                    </div>
                    <button
                        type="button"
                        onClick={handleAddField}
                        disabled={!selectedType}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
                    >
                        <Plus className="h-4 w-4" />
                        {t("label_agregar")}
                    </button>
                </div>
            </div>

            {/* Fields List */}
            <div className="space-y-3">
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                >
                    <SortableContext
                        items={fields.map((f) => f.id)}
                        strategy={verticalListSortingStrategy}
                    >
                        {fields.map((field) => (
                            <SortableFieldItem
                                key={field.id}
                                field={field}
                                onDelete={handleDeleteField}
                                onToggleEnabled={handleToggleEnabled}
                                onChange={handleFieldChange}
                            />
                        ))}
                    </SortableContext>
                </DndContext>

                {fields.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                        {t("label_no_hay_campos")}
                    </div>
                )}
            </div>
        </div>
    );
}

function SortableFieldItem({ field, onDelete, onToggleEnabled, onChange }) {
    const { t } = useTranslation();
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: field.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    // const getInputComponent = () => {
    //     switch (field.type) {
    //         case "text":
    //             return (
    //                 <input
    //                     type="text"
    //                     value={field.value || ""}
    //                     onChange={(e) => {
    //                         const value = e.target.value;
    //                         if (value.length <= 1024) {
    //                             onChange(field.id, "value", value);
    //                         }
    //                     }}
    //                     placeholder={t("placeholder_ingresa_texto")}
    //                     maxLength={1024}
    //                     disabled={!field.enabled}
    //                     className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:bg-gray-100 disabled:cursor-not-allowed"
    //                 />
    //             );
    //         case "number":
    //             return (
    //                 <input
    //                     type="text"
    //                     value={field.value || ""}
    //                     onChange={(e) => {
    //                         const value = e.target.value.replace(/[^0-9]/g, "");
    //                         if (value.length <= 7) {
    //                             onChange(field.id, "value", value);
    //                         }
    //                     }}
    //                     placeholder={t("placeholder_ingresa_numero")}
    //                     maxLength={7}
    //                     disabled={!field.enabled}
    //                     className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:bg-gray-100 disabled:cursor-not-allowed"
    //                 />
    //             );
    //         case "date":
    //             return (
    //                 <input
    //                     type="text"
    //                     value={field.value || ""}
    //                     onChange={(e) => onChange(field.id, "value", e.target.value)}
    //                     disabled={true}
    //                     placeholder={"YYYY/MM/DD"}
    //                     className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:bg-gray-100 disabled:cursor-not-allowed"
    //                 />
    //             );
    //         case "time":
    //             return (
    //                 <input
    //                     type="text"
    //                     value={field.value || ""}
    //                     onChange={(e) => onChange(field.id, "value", e.target.value)}
    //                     disabled={true}
    //                     placeholder={"HH:mm"}
    //                     className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:bg-gray-100 disabled:cursor-not-allowed"
    //                 />
    //             );
    //         case "date_time":
    //             return (
    //                 <input
    //                     type="text"
    //                     value={field.value || ""}
    //                     onChange={(e) => onChange(field.id, "value", e.target.value)}
    //                     disabled={true}
    //                     placeholder={"YYYY/MM/DD HH:mm"}
    //                     className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:bg-gray-100 disabled:cursor-not-allowed"
    //                 />
    //             );
    //         default:
    //             return null;
    //     }
    // };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`bg-white border rounded-lg p-4 shadow-sm ${!field.enabled ? "opacity-60" : ""
                }`}
        >
            <div className="flex items-start gap-3">
                {/* Drag Handle */}
                <div
                    {...attributes}
                    {...listeners}
                    className="cursor-grab active:cursor-grabbing mt-2 text-gray-400 hover:text-gray-600"
                >
                    <GripVertical className="h-5 w-5" />
                </div>

                {/* Field Content */}
                <div className="flex-1 space-y-3">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t("label_key")} <span className="text-xs text-gray-500">({t("label_only_letters_and_underscore_allowed")})</span><span className="text-xs text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            value={field.key || ""}
                            onChange={(e) => onChange(field.id, "key", e.target.value)}
                            placeholder={t("placeholder_ingresa_key")}
                            disabled={!field.enabled}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:bg-gray-100 disabled:cursor-not-allowed"
                            maxLength={64}
                        />
                        <p className="text-sm text-gray-500 flex justify-end">{field.key?.length}/64</p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t("label_etiqueta")}<span className="text-xs text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            value={field.label || ""}
                            onChange={(e) => onChange(field.id, "label", e.target.value)}
                            placeholder={t("placeholder_ingresa_etiqueta")}
                            disabled={!field.enabled}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:bg-gray-100 disabled:cursor-not-allowed"
                            maxLength={128}
                        />
                        <p className="text-sm text-gray-500 flex justify-end">{field.label?.length}/128</p>
                    </div>
                    {/* Label Input */}
                    {/* <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t("label_etiqueta")} <span className="text-xs text-gray-500">({t("label_only_letters_and_underscore_allowed")})</span>
                        </label>
                        <input
                            type="text"
                            value={field.label || ""}
                            onChange={(e) => onChange(field.id, "label", e.target.value)}
                            placeholder={t("placeholder_ingresa_etiqueta")}
                            disabled={!field.enabled}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:bg-gray-100 disabled:cursor-not-allowed"
                        />
                    </div> */}

                    {/* Type Display */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t("label_tipo")}
                        </label>
                        <div className="px-3 py-2 bg-gray-100 rounded-lg text-sm text-gray-600">
                            {field.type === "text" && (t("label_texto"))}
                            {field.type === "number" && (t("label_numero"))}
                            {field.type === "date" && (t("label_fecha"))}
                            {field.type === "time" && (t("label_hora"))}
                            {field.type === "date_time" && (t("label_date_time"))}
                        </div>
                    </div>

                    {/* Value Input */}
                    {/* <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t("label_placeholder")}
                        </label>
                        {getInputComponent()}
                        {field.type === "text" && (
                            <p className="text-xs text-gray-500 mt-1 text-right">
                                {(field.value || "").length}/1024
                            </p>
                        )}
                        {field.type === "number" && (
                            <p className="text-xs text-gray-500 mt-1 text-right">
                                {(field.value || "").length}/7
                            </p>
                        )}
                    </div> */}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-3 mt-2">
                    {/* Enable/Disable Switch */}
                    <Switch
                        checked={field.enabled}
                        onChange={() => onToggleEnabled(field.id)}
                        label={field.enabled ? t("label_habilitado") : t("label_deshabilitado")}
                    />

                    {/* Delete Button */}
                    <button
                        type="button"
                        onClick={() => onDelete(field.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title={t("label_eliminar")}
                    >
                        <Trash2 className="h-4 w-4" />
                    </button>
                </div>
            </div>
        </div>
    );
}

